import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MessageService } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { FirstLoginDto } from 'src/modules/utils/shared/first-login-dto.model';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { RestEndpoint } from '../../models/rest-endpoint.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { BackendResponse } from '../../shared/backend-response.model';

@Component({
    selector: 'app-first-login',
    templateUrl: './first-login.component.html',
    styleUrls: ['./first-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FirstLoginComponent extends ComponentView {

    static currentPassword = "";

    app = inject(MainAppService);
    message = inject(MessageService);

    form: FrontendForm;

    loaded = false;
    userName = "";
    running = false;
    done = false;

    pwText = "";

    async initParams(): Promise<boolean> {
        this.userName = this.route.snapshot.queryParams.userName;
        this.pwText = this.app.mainConfig.authRules.passwordRules.getRulesText();

        this.form = new FrontendForm([
            new FrontendField("currentPassword", "Aktuelles Passwort", FrontendFieldType.password, { mandatory: true,
                    value: FirstLoginComponent.currentPassword ?? ""}),
            new FrontendField("password", "Neues Passwort", FrontendFieldType.password, { mandatory: true },
            ),
            new FrontendField("passwordRepeat", "Neues Passwort wiederholen", FrontendFieldType.password, { mandatory: true },
            ),
            ...Arrays.mapWithIndex(this.app.mainConfig.authRules.useRecoveryQuestions ? this.app.mainConfig.authRules.authQuestions : [], (aq, index) => new FrontendField("authQuestion" + index, aq, FrontendFieldType.string)),
            new FrontendField("send", "Abschließen", FrontendFieldType.button, { 
                submitButton: true,
                icon: "fas fa-check",
                severity: "success",
                onClick: this.send.bind(this)
            }),
            new FrontendField("cancel", "Abbrechen", FrontendFieldType.button, {
                icon: "fas fa-times",
                severity: "danger",
                onClick: this.cancel.bind(this)
            })            
        ]);

        FirstLoginComponent.currentPassword = "";

        this.loaded = true;
        return Promise.resolve(true);
    }

    async send(): Promise<void> {
        let errors = this.form.validate();
        if (!Utils.isNoe(errors)) {
            this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: errors[0].message});
            return;
        }

        let pw1 = this.form.getValueString("password");
        let pw2 = this.form.getValueString("passwordRepeat");

        if (pw1 != pw2) {
            this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: "Die beiden neuen Passwörter stimmen nicht überein"});
            return;
        }

        if (!this.app.mainConfig.authRules.passwordRules.isSafe(pw1)) {
            this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: "Das neue Passwort stimmt nicht mit den Vorgaben überein. " + this.pwText});
            return;
        }

        let authQuestions = [];
        let authAnswers = [];
        if (this.app.mainConfig.authRules.useRecoveryQuestions) {
            for (let i = 0; i < this.app.mainConfig.authRules.authQuestions.length; i++) {
                if (!Utils.isNoe(this.form.getValueString("authQuestion" + i))) {
                    authQuestions.push(this.app.mainConfig.authRules.authQuestions[i]);
                    authAnswers.push(this.form.getValueString("authQuestion" + i));
                }
            }
            if (authQuestions.length < this.app.mainConfig.authRules.minRecoveryQuestions) {
                this.message.add({ severity: "error", summary: "Ungültige Angaben", life: 10000, detail: "Es müssen mindestens " + this.app.mainConfig.authRules.minRecoveryQuestions + " Fragen beantwortet werden."});
                return;
            }
        }

        this.running = true;
        try {
            await RestEndpoint.main().post().body(Objects.fromPlain(FirstLoginDto, {
                userName: this.userName,
                currentPassword: this.form.getValueString("currentPassword"),
                password: pw1,
                recoveryQuestions: authQuestions,
                recoveryAnswers: authAnswers
            })).run("api/app/auth/firstlogin").get(BackendResponse);
            this.message.add({ severity: "success", summary: "Die Aktivierung war erfolgreich.", life: 10000});
            Utils.setTimerOnce(2000, async () => {
                await this.app.navigateTo(["/base", "login"]);
            });
        }
        catch {
            this.message.add({ severity: "error", summary: "Die Aktivierung war nicht erfolgreich. Bitte prüfen Sie, ob das eingegebene aktuelle Passwort richtig ist", life: 10000});
        }
        this.running = false;
    }

    async cancel(): Promise<void> {
        await this.app.navigateTo(["/base", "login"]);
    }

}
